/**
 * Custom hook for managing Recruit CRM data and state
 *
 * Provides data fetching, state management for row selection, filters, sorting,
 * and modal states for the /recruit-crm page.
 */
import {useCallback, useMemo, useState} from 'react';
import type {
  Recruitment,
  RecruitmentFiltersState,
  RecruitmentsResponse,
  RecruitmentSortingState,
} from '@/views/creators/recruit-crm/types';
import useAgencyQuery from '@/hooks/queries/use-agency-query';
import useRecruitmentsQuery from '@/hooks/queries/use-recruitments-query';
import useBrandId from '@/hooks/use-brand-id';

export const PAGE_SELECTION_STATE = {
  none: 'none',
  partial: 'partial',
  all: 'all',
} as const;

export type PageSelectionState =
  (typeof PAGE_SELECTION_STATE)[keyof typeof PAGE_SELECTION_STATE];

/**
 * Modal visibility and state
 */
interface BulkEmailModalState {
  creatorIds: string[];
  isBulkAll: boolean;
}

interface BulkThreadModalState {
  creatorIds: string[];
  isBulkAll: boolean;
}

interface RemoveConfirmationModalState {
  recruitmentId: string;
}

interface BulkRemoveConfirmationModalState {
  recruitmentIds: string[];
}

// eslint-disable-next-line @typescript-eslint/no-empty-interface
interface RecruitByEmailModalState {}

interface BulkRequestVettingModalState {
  recruitmentIds: string[];
}

interface ModalsState {
  /** Bulk email modal state (null = closed) */
  bulkEmail: BulkEmailModalState | null;
  /** Bulk request vetting confirmation state (null = closed) */
  bulkRequestVetting: BulkRequestVettingModalState | null;
  /** Bulk thread modal state (null = closed) */
  bulkThread: BulkThreadModalState | null;
  /** Remove confirmation dialog state (null = closed) */
  removeConfirmation: RemoveConfirmationModalState | null;
  /** Bulk remove confirmation dialog state (null = closed) */
  bulkRemoveConfirmation: BulkRemoveConfirmationModalState | null;
  /** Recruit by email modal state (null = closed) */
  recruitByEmail: RecruitByEmailModalState | null;
}

/**
 * Complete state and actions for Recruit CRM page
 */
export interface RecruitCrmState {
  // ============================================================================
  // Data
  // ============================================================================

  /** Brand ID (undefined if not resolved) */
  brandId: string | undefined;
  /** Agency ID (undefined while loading) */
  agencyId: string | undefined;
  /** All fetched recruitment records (across all loaded pages) */
  recruitments: Recruitment[];
  /** Meta from the API response */
  meta: RecruitmentsResponse['meta'] | undefined;
  /** True on initial load */
  isLoading: boolean;
  /** True when fetching */
  isFetching: boolean;
  /** Load the next page */
  fetchNextPage: () => void;
  /** Whether more pages are available */
  hasNextPage: boolean | undefined;

  // ============================================================================
  // State
  // ============================================================================

  /** Row selection state (MRT format: Record<rowId, boolean>) */
  rowSelection: Record<string, boolean>;
  /** Filter state (status, removed) - matches API format */
  filters: RecruitmentFiltersState;
  /** Sorting state (sortBy, sortOrder) */
  sorting: RecruitmentSortingState;
  /** Modal visibility and state */
  modals: ModalsState;

  // ============================================================================
  // Row Selection Actions
  // ============================================================================

  /** Selection state of fetched rows for the header checkbox */
  pageSelectionState: PageSelectionState;
  /** Select or deselect a single row by id */
  toggleRowSelection: (id: string, selected: boolean) => void;
  /** Select all fetched rows */
  selectAll: () => void;
  /** Clear all selected rows */
  clearRowSelection: () => void;

  // ============================================================================
  // Filter Actions
  // ============================================================================

  /** Set filters (partial update, clears selection) */
  setFilters: (filters: Partial<RecruitmentFiltersState>) => void;
  /** Clear all filters to defaults (clears selection) */
  clearFilters: () => void;

  // ============================================================================
  // Sorting Actions
  // ============================================================================

  /** Set sorting (pass null to reset to default, clears selection) */
  setSorting: (sorting: RecruitmentSortingState | null) => void;
  /** Toggle sort order between asc/desc (clears selection) */
  toggleSortOrder: () => void;

  // ============================================================================
  // Modal Actions
  // ============================================================================

  /** Open bulk email modal */
  openBulkEmailModal: (state?: Partial<BulkEmailModalState>) => void;
  /** Close bulk email modal */
  closeBulkEmailModal: () => void;
  /** Open bulk thread modal */
  openBulkThreadModal: (state?: Partial<BulkThreadModalState>) => void;
  /** Close bulk thread modal */
  closeBulkThreadModal: () => void;
  /** Open bulk request vetting confirmation */
  openBulkRequestVetting: (recruitmentIds: string[]) => void;
  /** Close bulk request vetting confirmation */
  closeBulkRequestVetting: () => void;
  /** Open remove confirmation dialog */
  openRemoveConfirmation: (recruitmentId: string) => void;
  /** Close remove confirmation dialog */
  closeRemoveConfirmation: () => void;
  /** Open bulk remove confirmation dialog */
  openBulkRemoveConfirmation: (recruitmentIds: string[]) => void;
  /** Close bulk remove confirmation dialog */
  closeBulkRemoveConfirmation: () => void;
  /** Open recruit by email modal */
  openRecruitByEmailModal: () => void;
  /** Close recruit by email modal */
  closeRecruitByEmailModal: () => void;

  // ============================================================================
  // Utility Actions
  // ============================================================================

  /** Reset all state to defaults (except modals) */
  resetToDefaults: () => void;
}

const INITIAL_FILTERS: RecruitmentFiltersState = {
  status: [
    'invited',
    'pending_social_linking',
    'pending_vetting',
    'vetting_in_progress',
    'vetting_failed',
  ],
};

const INITIAL_SORTING: RecruitmentSortingState = {
  sortBy: 'created_at',
  sortOrder: 'desc',
};

const INITIAL_MODALS: ModalsState = {
  bulkEmail: null,
  bulkRequestVetting: null,
  bulkThread: null,
  removeConfirmation: null,
  bulkRemoveConfirmation: null,
  recruitByEmail: null,
};

/**
 * Hook to manage Recruit CRM data and state
 *
 * @returns Data, state and actions for the recruit CRM page
 */
export function useRecruitCrmState(): RecruitCrmState {
  const [rowSelection, setRowSelectionState] = useState<
    Record<string, boolean>
  >({});
  const [filters, setFiltersState] =
    useState<RecruitmentFiltersState>(INITIAL_FILTERS);
  const [sorting, setSortingState] =
    useState<RecruitmentSortingState>(INITIAL_SORTING);
  const [modals, setModalsState] = useState<ModalsState>(INITIAL_MODALS);

  // Data fetching
  const brandId = useBrandId();
  const {data: agencyData} = useAgencyQuery({brandId: brandId || ''});
  const agencyId = agencyData?.id;

  const {
    recruitments,
    meta,
    isLoading,
    isFetching,
    fetchNextPage,
    hasNextPage,
  } = useRecruitmentsQuery({
    agencyId: agencyId || '',
    status: filters.status,
    sortBy: sorting.sortBy,
    sortOrder: sorting.sortOrder,
    perPage: 25,
    enabled: !!agencyId,
  });

  // Row selection actions
  const toggleRowSelection = useCallback((id: string, selected: boolean) => {
    setRowSelectionState((prev) =>
      selected
        ? {...prev, [id]: true}
        : Object.fromEntries(Object.entries(prev).filter(([key]) => key !== id))
    );
  }, []);

  const clearRowSelection = useCallback(() => {
    setRowSelectionState({});
  }, []);

  const selectAll = useCallback(() => {
    setRowSelectionState(
      Object.fromEntries(recruitments.map((r) => [r.id, true]))
    );
  }, [recruitments]);

  const pageSelectionState = useMemo((): PageSelectionState => {
    const totalCount = recruitments.length;
    if (totalCount === 0) {
      return PAGE_SELECTION_STATE.none;
    }
    const selectedCount = recruitments.filter((r) => rowSelection[r.id]).length;
    if (selectedCount === totalCount) {
      return PAGE_SELECTION_STATE.all;
    }
    if (selectedCount > 0) {
      return PAGE_SELECTION_STATE.partial;
    }
    return PAGE_SELECTION_STATE.none;
  }, [recruitments, rowSelection]);

  // Filter actions
  const setFilters = useCallback(
    (newFilters: Partial<RecruitmentFiltersState>) => {
      setFiltersState((prev) => ({...prev, ...newFilters}));
      clearRowSelection();
    },
    [clearRowSelection]
  );

  const clearFilters = useCallback(() => {
    setFiltersState(INITIAL_FILTERS);
    clearRowSelection();
  }, [clearRowSelection]);

  // Sorting actions
  const setSorting = useCallback(
    (newSorting: RecruitmentSortingState | null) => {
      if (newSorting === null) {
        // Reset to default sorting
        setSortingState(INITIAL_SORTING);
      } else {
        setSortingState(newSorting);
      }
      clearRowSelection();
    },
    [clearRowSelection]
  );

  const toggleSortOrder = useCallback(() => {
    setSortingState((prev) => ({
      ...prev,
      sortOrder: prev.sortOrder === 'asc' ? 'desc' : 'asc',
    }));
    clearRowSelection();
  }, [clearRowSelection]);

  // Modal actions
  const openBulkEmailModal = useCallback(
    (state?: Partial<BulkEmailModalState>) => {
      setModalsState((prev) => ({
        ...prev,
        bulkEmail: {
          creatorIds: state?.creatorIds ?? [],
          isBulkAll: state?.isBulkAll ?? false,
        },
      }));
    },
    []
  );

  const closeBulkEmailModal = useCallback(() => {
    setModalsState((prev) => ({...prev, bulkEmail: null}));
  }, []);

  const openBulkThreadModal = useCallback(
    (state?: Partial<BulkThreadModalState>) => {
      setModalsState((prev) => ({
        ...prev,
        bulkThread: {
          creatorIds: state?.creatorIds ?? [],
          isBulkAll: state?.isBulkAll ?? false,
        },
      }));
    },
    []
  );

  const closeBulkThreadModal = useCallback(() => {
    setModalsState((prev) => ({...prev, bulkThread: null}));
  }, []);

  const openBulkRequestVetting = useCallback((recruitmentIds: string[]) => {
    setModalsState((prev) => ({
      ...prev,
      bulkRequestVetting: {recruitmentIds},
    }));
  }, []);

  const closeBulkRequestVetting = useCallback(() => {
    setModalsState((prev) => ({...prev, bulkRequestVetting: null}));
  }, []);

  const openRemoveConfirmation = useCallback((recruitmentId: string) => {
    setModalsState((prev) => ({
      ...prev,
      removeConfirmation: {recruitmentId},
    }));
  }, []);

  const closeRemoveConfirmation = useCallback(() => {
    setModalsState((prev) => ({
      ...prev,
      removeConfirmation: null,
    }));
  }, []);

  const openBulkRemoveConfirmation = useCallback((recruitmentIds: string[]) => {
    setModalsState((prev) => ({
      ...prev,
      bulkRemoveConfirmation: {recruitmentIds},
    }));
  }, []);

  const closeBulkRemoveConfirmation = useCallback(() => {
    setModalsState((prev) => ({
      ...prev,
      bulkRemoveConfirmation: null,
    }));
  }, []);

  const openRecruitByEmailModal = useCallback(() => {
    setModalsState((prev) => ({...prev, recruitByEmail: {}}));
  }, []);

  const closeRecruitByEmailModal = useCallback(() => {
    setModalsState((prev) => ({...prev, recruitByEmail: null}));
  }, []);

  // Utility
  const resetToDefaults = useCallback(() => {
    setRowSelectionState({});
    setFiltersState(INITIAL_FILTERS);
    setSortingState(INITIAL_SORTING);
    // Note: Does not reset modals (intentional - user might have dialogs open)
  }, []);

  return useMemo(
    () => ({
      // Data
      brandId,
      agencyId,
      recruitments,
      meta,
      isLoading,
      isFetching,
      fetchNextPage,
      hasNextPage,

      // State
      rowSelection,
      filters,
      sorting,
      modals,

      // Actions
      pageSelectionState,
      toggleRowSelection,
      selectAll,
      clearRowSelection,
      setFilters,
      clearFilters,
      setSorting,
      toggleSortOrder,
      openBulkEmailModal,
      closeBulkEmailModal,
      openBulkRequestVetting,
      closeBulkRequestVetting,
      openBulkThreadModal,
      closeBulkThreadModal,
      openRemoveConfirmation,
      closeRemoveConfirmation,
      openBulkRemoveConfirmation,
      closeBulkRemoveConfirmation,
      openRecruitByEmailModal,
      closeRecruitByEmailModal,
      resetToDefaults,
    }),
    [
      brandId,
      agencyId,
      recruitments,
      meta,
      isLoading,
      isFetching,
      fetchNextPage,
      hasNextPage,
      rowSelection,
      filters,
      sorting,
      modals,
      pageSelectionState,
      toggleRowSelection,
      selectAll,
      clearRowSelection,
      setFilters,
      clearFilters,
      setSorting,
      toggleSortOrder,
      openBulkEmailModal,
      closeBulkEmailModal,
      openBulkRequestVetting,
      closeBulkRequestVetting,
      openBulkThreadModal,
      closeBulkThreadModal,
      openRemoveConfirmation,
      closeRemoveConfirmation,
      openBulkRemoveConfirmation,
      closeBulkRemoveConfirmation,
      openRecruitByEmailModal,
      closeRecruitByEmailModal,
      resetToDefaults,
    ]
  );
}
